import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Employee, ProJob, TimeEntry } from '../../types/pro';
import { JOB_STATUS, addDays, formatDate, formatTime, isSameDay, startOfWeek } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProEmployeesProps {
  employees: Employee[];
  jobs: ProJob[];
  timeEntries: TimeEntry[];
  onOpenJob: (jobId: string) => void;
}
export function ProEmployees({
  employees,
  jobs,
  timeEntries,
  onOpenJob
}: ProEmployeesProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const stats = useMemo(() => {
    const monday = startOfWeek(new Date());
    const sunday = addDays(monday, 7);
    const now = new Date();
    return new Map(employees.map(employee => {
      const week = timeEntries.filter(entry => {
        const date = new Date(entry.date);
        return entry.employeeId === employee.id && date >= monday && date < sunday;
      }).reduce((sum, entry) => sum + entry.hours, 0);
      const own = jobs.filter(job => job.employeeIds.includes(employee.id ?? ''));
      const current = own.find(job => job.status === 'i-gang' && job.start && isSameDay(new Date(job.start), now));
      const todayBooked = own.some(job => job.start && isSameDay(new Date(job.start), now));
      return [employee.id ?? '', {
        week,
        jobs: own,
        status: current ? 'På opgave' : todayBooked ? 'Booket i dag' : 'Ledig',
        statusTone: current ? 'bg-amber-50 text-amber-700' : todayBooked ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
      }];
    }));
  }, [employees, jobs, timeEntries]);
  const open = employees.find(employee => employee.id === openId);
  if (open) {
    const data = stats.get(open.id ?? '');
    const upcoming = (data?.jobs ?? []).filter(job => job.start && new Date(job.start) >= new Date()).sort((a, b) => (a.start ?? '').localeCompare(b.start ?? ''));
    const hours = timeEntries.filter(entry => entry.employeeId === open.id);
    return <div data-id="element-855" data-mp="2fnmiecyio4d9">
        <button onClick={() => setOpenId(null)} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-856" data-mp="2fnw4hpgfxrdo">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-857" data-mp="2okn0k1uzt689" />
          Alle medarbejdere
        </button>

        <header className="mb-5 flex items-center gap-4" data-id="element-858" data-mp="20vc95azj8o6s">
          <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full text-lg font-bold text-white" style={{
          backgroundColor: open.color
        }} data-id="element-859" data-mp="1vyu4sc846ruz">
            {open.name.split(' ').map(part => part[0]).join('').slice(0, 2)}
          </span>
          <div className="min-w-0" data-id="element-860" data-mp="2dij0swypsud">
            <h1 className="text-2xl font-bold tracking-tight text-stone-900" data-id="element-861" data-mp="7dn0om9lwtm6">
              {open.name}
            </h1>
            <p className="text-sm text-stone-500" data-id="element-862" data-mp="v5bwicln007v">{open.role}</p>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-863" data-mp="z4b09tvmtqxq">
          <section className="rounded-xl border border-stone-200 bg-white p-5" data-id="element-864" data-mp="l634hpac0vo5">
            <h2 className="mb-3 text-base font-semibold text-stone-900" data-id="element-865" data-mp="q4xo2tg4io9b">
              Kontakt
            </h2>
            <ul className="space-y-2.5 text-sm" data-id="element-866" data-mp="35wbsatipjviq">
              <li data-id="element-867" data-mp="35uh6ze3iy1e4">
                <a href={`tel:${open.phone.replace(/\s/g, '')}`} className="flex items-center gap-2.5 text-stone-700 transition-colors hover:text-brand-700" data-id="element-868" data-mp="35y52gisj4xvh">
                  <PhoneIcon className="h-4 w-4 text-stone-400" data-id="element-869" data-mp="3q6mmllvdtv8g" />
                  {open.phone}
                </a>
              </li>
              <li data-id="element-870" data-mp="3hbouui3z0oip">
                <a href={`mailto:${open.email}`} className="flex items-center gap-2.5 break-all text-stone-700 transition-colors hover:text-brand-700" data-id="element-871" data-mp="3hd9q69y40bhm">
                  <MailIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-872" data-mp="cxqc4xsd38ao" />
                  {open.email}
                </a>
              </li>
              <li className="border-t border-stone-100 pt-2.5" data-id="element-873" data-mp="3xljjtrtuegk">
                <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-874" data-mp="3vqwa8t32a9b">
                  Timepris
                </span>
                <span className="text-stone-900" data-id="element-875" data-mp="2onj9pu908xic">{open.hourlyRate} kr/t</span>
              </li>
              <li data-id="element-876" data-mp="2jozk9jv7d3l8">
                <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-877" data-mp="2jogd8y73bzlz">
                  Timer denne uge
                </span>
                <span className="text-stone-900" data-id="element-878" data-mp="29mm2zywxynxr">
                  {(data?.week ?? 0).toLocaleString('da-DK')} t
                </span>
              </li>
            </ul>
          </section>

          <section className="overflow-hidden rounded-xl border border-stone-200 bg-white lg:col-span-2" data-id="element-879" data-mp="2jhnuv2ilq441">
            <header className="border-b border-stone-100 px-5 py-3.5" data-id="element-880" data-mp="2ob09cjtkguj2">
              <h2 className="text-base font-semibold text-stone-900" data-id="element-881" data-mp="2sahch6litfl2">
                Kommende opgaver
              </h2>
            </header>
            {upcoming.length === 0 ? <EmptyState title="Ingen kommende opgaver" data-id="element-882" data-mp="3296xv9cmli62" /> : <ul className="divide-y divide-stone-100" data-id="element-883" data-mp="3789rzyr2i3z9">
                {upcoming.map(job => <li key={job.id} data-id="element-884" data-mp="17vp6tcd0i36a">
                    <button onClick={() => job.id && onOpenJob(job.id)} className="flex w-full items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-stone-50" data-id="element-885" data-mp="17wqssbvkga2s">
                      <span className="min-w-0 flex-1" data-id="element-886" data-mp="14vnnshamz8ic">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-887" data-mp="zw1bca2mtwal">
                          {job.title}
                        </span>
                        <span className="block text-xs text-stone-500" data-id="element-888" data-mp="19t721h7c3laa">
                          {formatDate(job.start ?? '')}{' '}
                          {formatTime(job.start ?? '')} · {job.customerName}
                        </span>
                      </span>
                      <StatusChip meta={JOB_STATUS[job.status]} data-id="element-889" data-mp="1irj2eay05xfx" />
                    </button>
                  </li>)}
              </ul>}
          </section>

          <section className="overflow-hidden rounded-xl border border-stone-200 bg-white lg:col-span-3" data-id="element-890" data-mp="1sjx2kz9fmvzz">
            <header className="border-b border-stone-100 px-5 py-3.5" data-id="element-891" data-mp="26jg06y4maoy1">
              <h2 className="text-base font-semibold text-stone-900" data-id="element-892" data-mp="22iw47xvs5rdh">
                Registrerede timer
              </h2>
            </header>
            {hours.length === 0 ? <EmptyState title="Ingen registrerede timer" data-id="element-893" data-mp="2q584cjaxt6bv" /> : <ul className="divide-y divide-stone-100" data-id="element-894" data-mp="2c5gumurgec3o">
                {hours.map(entry => {
              const job = jobs.find(j => j.id === entry.jobId);
              return <li key={entry.id} className="flex items-center gap-4 px-5 py-3" data-id="element-895" data-mp="22fnle0esa92l">
                      <span className="min-w-0 flex-1 truncate text-sm text-stone-900" data-id="element-896" data-mp="277oqn2djazj4">
                        {job?.title ?? 'Opgave'}
                      </span>
                      <span className="text-xs text-stone-500" data-id="element-897" data-mp="1xfj2y4ubf49q">
                        {entry.workType} · {formatDate(entry.date)}
                      </span>
                      <span className="w-16 text-right text-sm font-semibold tabular-nums text-stone-900" data-id="element-898" data-mp="1jivub4t02pnz">
                        {entry.hours.toLocaleString('da-DK')} t
                      </span>
                    </li>;
            })}
              </ul>}
          </section>
        </div>
      </div>;
  }
  return <div data-id="element-899" data-mp="1uvwbs7d7ks6o">
      <PageHeader title="Medarbejdere" subtitle={`${employees.filter(e => e.active).length} aktive`} data-id="element-900" data-mp="1uve9ou3v7jqu" />

      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white" data-id="element-901" data-mp="1q07c8416vbad">
        <table className="w-full min-w-[680px] text-sm" data-id="element-902" data-mp="1l02ukaojoaik">
          <thead data-id="element-903" data-mp="1g7t66z32viie">
            <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-904" data-mp="1g79q7nwzn470">
              {['Navn', 'Rolle', 'Telefon', 'E-mail', 'Status', 'Timer denne uge'].map(heading => <th key={heading} scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-905" data-mp="126yxefc4bfj7">
                  {heading}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100" data-id="element-906" data-mp="3duc6tgxg8lhr">
            {employees.map(employee => {
            const data = stats.get(employee.id ?? '');
            return <tr key={employee.id} onClick={() => setOpenId(employee.id ?? null)} className="cursor-pointer transition-colors hover:bg-stone-50" data-id="element-907" data-mp="3mwli490jkiw7">
                  <td className="px-4 py-3" data-id="element-908" data-mp="2v5xemqbeb1fd">
                    <span className="flex items-center gap-2.5" data-id="element-909" data-mp="2z6r61to1qyd0">
                      <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{
                    backgroundColor: employee.color
                  }} data-id="element-910" data-mp="2z5g0ppeydijs" />
                      <span className="font-medium text-stone-900" data-id="element-911" data-mp="27f1r45dz2sdx">
                        {employee.name}
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-stone-600" data-id="element-912" data-mp="623pn28wcv9u">{employee.role}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-stone-600" data-id="element-913" data-mp="b16twkh1yxgr">
                    {employee.phone}
                  </td>
                  <td className="max-w-[200px] truncate px-4 py-3 text-stone-600" data-id="element-914" data-mp="f4cwni4yi6ih">
                    {employee.email}
                  </td>
                  <td className="px-4 py-3" data-id="element-915" data-mp="3ji1kp1kksreh">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${data?.statusTone}`} data-id="element-916" data-mp="3jjct5eoyunbx">
                      {data?.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold tabular-nums text-stone-900" data-id="element-917" data-mp="13ip11pqg9kv">
                    {(data?.week ?? 0).toLocaleString('da-DK')} t
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}